// client/src/pages/Dashboard/components/LocationDataPanel.js
// Detaljpanel för valt område + symptomkombination. Hämtar och visar
// rapportcounts per scope (postnummer/stad/land).
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import API_CONFIG from '../../../config/apiConfig';
import './LocationDataPanel.css';

const LocationDataPanel = ({
  geoScope,
  selectedCity,
  confirmedPostalCode,
  selectedSymptoms,
  selectedWeek
}) => {
  const { t } = useTranslation();

  // ✅ Symptom translation helper
  const tSymptom = (name) => t(`symptoms.${name}`, name);
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Labels
  const areaLabel = geoScope === 'postal' && confirmedPostalCode
    ? `${confirmedPostalCode} ${selectedCity || ''}`
    : geoScope === 'city' && selectedCity
      ? selectedCity
      : t('common.sweden');

  const symptomLabel = selectedSymptoms.length > 0 
    ? selectedSymptoms.map(s => tSymptom(s)).join(' + ') 
    : null;

  // Hämta data när filter ändras
  const fetchData = useCallback(async () => {
    if (selectedSymptoms.length === 0) {
      setData(null);
      return;
    }

    setLoading(true);

    try {
      const symptomsParam = encodeURIComponent(selectedSymptoms.join(','));
      const startWeek = Math.max(1, selectedWeek - 3);

      // Bygg URL för postal-groups
      const buildUrl = (city, postal) => {
        let url = `${API_CONFIG.baseUrl}/health-data/postal-groups?week=${selectedWeek}&symptoms=${symptomsParam}&matchMode=combination`;
        if (postal) url += `&postalCode=${postal}`;
        else if (city) url += `&city=${encodeURIComponent(city)}`;
        return url;
      };

      // Parallella anrop
      const calls = [
        // Riksnivå
        fetch(buildUrl()).then(r => r.json()),
        // Trend för valt område
        fetch(`${API_CONFIG.baseUrl}/health-data/trend?startWeek=${startWeek}&endWeek=${selectedWeek}&symptoms=${symptomsParam}&scope=${geoScope}${selectedCity ? `&city=${encodeURIComponent(selectedCity)}` : ''}${confirmedPostalCode ? `&postalCode=${confirmedPostalCode}` : ''}`).then(r => r.json())
      ];

      // Lägg till stad om vald
      if (selectedCity) {
        calls.push(fetch(buildUrl(selectedCity)).then(r => r.json()));
      }

      // Lägg till postnummer om vald
      if (confirmedPostalCode) {
        calls.push(fetch(buildUrl(selectedCity, confirmedPostalCode)).then(r => r.json()));
      }

      const results = await Promise.all(calls);
      
      // Processera resultat
      const processResult = (result) => {
        if (!result.success || !result.data) return { count: 0, total: 0, prevalence: 0 };
        const count = result.data.reduce((sum, item) => sum + (item.symptomCount || item.count || 0), 0);
        const total = result.data.reduce((sum, item) => sum + (item.totalCount || 0), 0);
        return {
          count,
          total,
          prevalence: total > 0 ? Math.round((count / total) * 100) : 0
        };
      };

      const countryData = processResult(results[0]);
      const trendResult = results[1];
      const cityData = selectedCity ? processResult(results[2]) : null;
      const postalData = confirmedPostalCode ? processResult(results[selectedCity ? 3 : 2]) : null;

      // Beräkna trend
      let trendPercent = 0;
      let trendDirection = 'stable';
      if (trendResult.success && trendResult.data && trendResult.data.length >= 2) {
        const weeks = trendResult.data.sort((a, b) => a.week - b.week);
        const firstWeek = weeks[0]?.matchingReports || 0;
        const lastWeek = weeks[weeks.length - 1]?.matchingReports || 0;
        if (firstWeek > 0) {
          trendPercent = Math.round(((lastWeek - firstWeek) / firstWeek) * 100);
          trendDirection = trendPercent > 5 ? 'up' : trendPercent < -5 ? 'down' : 'stable';
        }
      }

      // Välj primär data baserat på scope
      const primaryData = postalData || cityData || countryData;

      setData({
        primary: primaryData,
        country: countryData,
        city: cityData,
        postal: postalData,
        trend: {
          percent: Math.abs(trendPercent),
          direction: trendDirection,
          weeks: trendResult.success ? trendResult.data : []
        }
      });

    } catch (error) {
      console.error('❌ LocationDataPanel fetch error:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [geoScope, selectedCity, confirmedPostalCode, selectedSymptoms, selectedWeek]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Visa placeholder om inga symptom valda
  if (selectedSymptoms.length === 0) {
    return (
      <section className="location-data-panel placeholder">
        <div className="placeholder-content">
          <div className="placeholder-icon">🔬</div>
          <h3 className="placeholder-title">{t('locationPanel.placeholderTitle')}</h3>
          <p className="placeholder-text">
            {t('locationPanel.placeholderText')}
          </p>
        </div>
      </section>
    );
  }

  // Laddar
  if (loading) {
    return (
      <section className="location-data-panel loading">
        <div className="loading-content">{t('locationPanel.loading')}</div>
      </section>
    );
  }

  // Ingen data
  if (!data) {
    return null;
  }

  // Render trend-graf - ✅ FIXAD: Använder pixlar istället för procent
  const renderMiniTrend = () => {
    if (!data.trend.weeks || data.trend.weeks.length < 2) return null;
    
    const weeks = data.trend.weeks.sort((a, b) => a.week - b.week);
    const maxCount = Math.max(...weeks.map(w => w.matchingReports || 0));
    const maxBarHeight = 60; // pixlar för tydlig höjdskillnad
    
    return (
      <div className="mini-trend-chart">
        {weeks.map((week) => {
          const heightPx = maxCount > 0 ? Math.round((week.matchingReports / maxCount) * maxBarHeight) : 0;
          return (
            <div key={week.week} className="trend-bar-container">
              <div 
                className="trend-bar" 
                style={{ height: `${Math.max(heightPx, 4)}px` }}
                title={t('locationPanel.weekTooltip', { week: week.week, count: week.matchingReports })}
              />
              <span className="trend-week-label">{t('locationPanel.weekLabel', { week: week.week })}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <section className="location-data-panel">
      
      {/* HEADER: Valt område + symptom */}
      <div className="panel-header">
        <div className="selection-summary">
          <span className="area-badge">📍 {areaLabel}</span>
          <span className="symptom-badge">🔬 {symptomLabel}</span>
        </div>
      </div>

      {/* STATS CARDS */}
      <div className="stats-row">
        
        {/* Antal rapporter */}
        <div className="stat-card primary">
          <div className="stat-value">{data.primary.count}</div>
          <div className="stat-label">{t('locationPanel.reports')}</div>
          <div className="stat-sublabel">{t('locationPanel.thisWeek')}</div>
        </div>

        {/* Trend */}
        <div className={`stat-card trend ${data.trend.direction}`}>
          <div className="stat-value">
            {data.trend.direction === 'up' && '▲ '}
            {data.trend.direction === 'down' && '▼ '}
            {data.trend.direction === 'stable' && '― '}
            {data.trend.percent}%
          </div>
          <div className="stat-label">
            {data.trend.direction === 'up' && t('locationPanel.trendUp')}
            {data.trend.direction === 'down' && t('locationPanel.trendDown')}
            {data.trend.direction === 'stable' && t('locationPanel.trendStable')}
          </div>
          <div className="stat-sublabel">{t('locationPanel.lastFourWeeks')}</div>
        </div>

        {/* Prevalens */}
        <div className="stat-card">
          <div className="stat-value">{data.primary.prevalence}%</div>
          <div className="stat-label">{t('locationPanel.ofAll')}</div>
          <div className="stat-sublabel">{t('locationPanel.inArea')}</div>
        </div>

      </div>

      {/* JÄMFÖRELSE */}
      <div className="comparison-section">
        <h4 className="comparison-title">{t('locationPanel.comparison')}</h4>
        <div className="comparison-bars">
          
          {/* Postnummer */}
          {data.postal && (
            <div className="comparison-row">
              <span className="comparison-label">{confirmedPostalCode}</span>
              <div className="comparison-bar-track">
                <div 
                  className="comparison-bar-fill postal"
                  style={{ width: `${Math.min(data.postal.prevalence * 2, 100)}%` }}
                />
              </div>
              <span className="comparison-value">{data.postal.prevalence}%</span>
            </div>
          )}

          {/* Stad */}
          {data.city && (
            <div className="comparison-row">
              <span className="comparison-label">{selectedCity}</span>
              <div className="comparison-bar-track">
                <div 
                  className="comparison-bar-fill city"
                  style={{ width: `${Math.min(data.city.prevalence * 2, 100)}%` }}
                />
              </div>
              <span className="comparison-value">{data.city.prevalence}%</span>
            </div>
          )}

          {/* Sverige */}
          <div className="comparison-row">
            <span className="comparison-label">{t('common.sweden')}</span>
            <div className="comparison-bar-track">
              <div 
                className="comparison-bar-fill country"
                style={{ width: `${Math.min(data.country.prevalence * 2, 100)}%` }}
              />
            </div>
            <span className="comparison-value">{data.country.prevalence}%</span>
          </div>

        </div>
      </div>

      {/* TREND GRAF */}
      <div className="trend-section">
        <h4 className="trend-title">{t('locationPanel.development')}</h4>
        {renderMiniTrend()}
      </div>

    </section>
  );
};

export default LocationDataPanel;
